import { ContactUsLink } from '@components/ContactUsLink/ContactUsLink';
import { Tooltip } from '@mui/material';
import {
  MediaStatusType,
  OVER_DURATION_LIMIT_CONTACT_MESSAGE,
} from '@stores/UploadURL/mediaStatus';
import { memo, ReactNode } from 'react';
import css from './MediaDataGrid.module.scss';

const STATUS_LABELS: Record<MediaStatusType, string> = {
  error: 'Error',
  warning: 'Warning',
  'in-library': 'In library',
};

export const STATUS_TOOLTIPS: Record<MediaStatusType, string> = {
  error: 'This media exceeds the 10-hour limit. Contact us if you need a higher limit.',
  warning: "This media is much longer than the others you've added.",
  'in-library': 'Already in this library. Now you can use those credits for something else!',
};

/**
 * Tooltip content for a media status. Same copy as STATUS_TOOLTIPS, but the
 * error tooltip's "Contact us" is a clickable Intercom link (MUI tooltips are
 * interactive, so the link is reachable). Use this for rendering; the plain
 * STATUS_TOOLTIPS map remains for any string-only needs.
 */
export function getStatusTooltip(status: MediaStatusType): ReactNode {
  if (status === 'error') {
    return (
      <>
        This media exceeds the 10-hour limit.{' '}
        <ContactUsLink message={OVER_DURATION_LIMIT_CONTACT_MESSAGE} /> if you need a higher limit.
      </>
    );
  }
  return STATUS_TOOLTIPS[status];
}

const STATUS_CLASS_MAP: Record<MediaStatusType, string> = {
  error: css.statusBadgeError,
  warning: css.statusBadgeWarning,
  'in-library': css.inLibraryBadge,
};

interface MediaStatusBadgeProps {
  status: MediaStatusType | null;
}

export const MediaStatusBadge = memo<MediaStatusBadgeProps>(({ status }) => {
  if (!status) return null;

  return (
    <Tooltip title={getStatusTooltip(status)} arrow placement="top">
      <span className={STATUS_CLASS_MAP[status]}>{STATUS_LABELS[status]}</span>
    </Tooltip>
  );
});

MediaStatusBadge.displayName = 'MediaStatusBadge';
